import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { cell, monthLabel } from "./format";

const Cells = ({ arr, total, onCell, bold }) => (
  <>
    {arr.map((v, i) => (
      <td key={i} className={`px-2 py-1.5 text-right font-mono ${v ? "cursor-pointer hover:bg-amber-100" : "text-slate-400"} ${bold ? "font-bold" : ""}`}
        onClick={() => v && onCell(i)}>{cell(v)}</td>
    ))}
    <td className={`px-2 py-1.5 text-right font-mono bg-slate-50 ${total ? "cursor-pointer hover:bg-amber-100" : "text-slate-400"} font-semibold`}
      onClick={() => total && onCell(null)}>{cell(total)}</td>
  </>
);

const Header = ({ months, first }) => (
  <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
    <tr className="border-b">
      <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 min-w-[220px]">{first}</th>
      {months.map(m => <th key={m} className="text-right px-2 py-2 whitespace-nowrap">{monthLabel(m).split(" ")[0]}</th>)}
      <th className="text-right px-2 py-2">FY Total</th>
    </tr>
  </thead>
);

const Footer = ({ data, open }) => (
  <>
    <tr className="border-t-2 border-slate-400 bg-slate-100" data-testid="monthly-total-row">
      <td className="px-3 py-2 font-bold sticky left-0 bg-slate-100">Total</td>
      <Cells arr={data.total} total={data.fy_total} bold onCell={(i) => open({}, i, "Total")} />
    </tr>
    {Object.entries(data.source).map(([k, arr]) => (
      <tr key={k} className="text-xs text-slate-600" data-testid={`monthly-source-${k === "Vendor bills" ? "vendor" : "rmb"}`}>
        <td className="px-3 py-1 pl-6 sticky left-0 bg-white">{k}</td>
        <Cells arr={arr} total={arr.reduce((a, b) => a + b, 0)}
          onCell={(i) => open({ source: k === "Vendor bills" ? "Vendor bill" : "Reimbursement" }, i, k)} />
      </tr>
    ))}
  </>
);

export const MonthlyTable = ({ data, onCell, onPickProject }) => {
  const open = (filter, i, title) => onCell({ ...filter, month: i === null ? null : data.months[i] }, title);
  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs" data-testid="report-monthly-table">
          <Header months={data.months} first={data.projects ? "Project" : "Line"} />
          <tbody>
            {data.projects ? data.projects.map(p => (
              <tr key={p.project_id} className="border-b hover:bg-slate-50" data-testid={`monthly-project-${p.project_code}`}>
                <td className="px-3 py-1.5 sticky left-0 bg-white">
                  <button className="text-blue-600 font-semibold" onClick={() => onPickProject(p.project_id)} data-testid={`monthly-project-link-${p.project_code}`}>{p.project_code}</button> · {p.project_name}
                </td>
                <Cells arr={p.months} total={p.total} onCell={(i) => onCell({ project_id: p.project_id, month: i === null ? null : data.months[i] }, p.project_code)} />
              </tr>
            )) : data.categories.map(c => (
              <React.Fragment key={c.code}>
                <tr className="bg-slate-100 font-semibold uppercase">
                  <td className="px-3 py-1.5 sticky left-0 bg-slate-100">{c.code} · {c.name}</td>
                  <Cells arr={c.totals.months} total={c.totals.total} onCell={(i) => open({ category_code: c.code }, i, c.code)} />
                </tr>
                {c.heads.map(h => (
                  <React.Fragment key={h.id}>
                    <tr className="bg-slate-50 border-b font-medium">
                      <td className="px-3 py-1.5 pl-6 sticky left-0 bg-slate-50">{h.code} · {h.name}</td>
                      <Cells arr={h.totals.months} total={h.totals.total} onCell={(i) => open({ head_id: h.id }, i, h.code)} />
                    </tr>
                    {h.subtypes.map(s => (
                      <tr key={s.id} className="border-b" data-testid={`monthly-sub-${s.code}`}>
                        <td className="px-3 py-1.5 pl-10 sticky left-0 bg-white"><span className="font-mono">{s.code}</span> · {s.name}</td>
                        <Cells arr={s.months} total={s.total} onCell={(i) => open({ subtype_id: s.id }, i, s.code)} />
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            <Footer data={data} open={open} />
          </tbody>
        </table>
        {data.unmapped > 0 && <p className="text-[11px] text-amber-700 p-2">Includes {cell(data.unmapped)} booked to sub-types no longer in this budget (shown in Total only).</p>}
      </CardContent>
    </Card>
  );
};
